"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import type { DocTopic } from "@/lib/docs/registry";

interface HeadingLink {
  id: string;
  label: string;
}

export function DocTableOfContents({ rootId }: { rootId: string }) {
  const [headings, setHeadings] = useState<HeadingLink[]>([]);

  useEffect(() => {
    const root = document.getElementById(rootId);
    if (!root) return;

    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = Array.from(root.querySelectorAll<HTMLHeadingElement>("h2[id]"))
          .map((heading) => ({
            id: heading.id,
            label: heading.childNodes[0]?.textContent?.trim() || heading.textContent?.replace(/#$/, "").trim() || heading.id,
          }));
        setHeadings(next);
      });
    };

    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [rootId]);

  if (headings.length < 2) return null;
  return (
    <nav aria-label="In this article" className="mb-6 rounded-lg border border-spark-light-gray bg-spark-hover px-4 py-3">
      <p className="text-[11px] font-bold uppercase tracking-[.08em] text-spark-gray">In this article</p>
      <ul className="mt-2 grid gap-x-5 gap-y-1 sm:grid-cols-2">
        {headings.map((heading) => (
          <li key={heading.id}>
            <a href={`#${heading.id}`} className="text-sm text-spark-blue-text hover:underline">
              {heading.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function reviewedDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${months[month - 1] ?? month} ${day}, ${year}`;
}

export function DocArticleFooter({
  slug,
  updatedAt,
  related,
}: {
  slug: string;
  updatedAt: string;
  related: readonly DocTopic[];
}) {
  const [feedback, setFeedback] = useState<"idle" | "sending" | "saved" | "error">("idle");

  async function sendFeedback(helpful: boolean) {
    setFeedback("sending");
    try {
      const response = await fetch("/api/docs/feedback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ slug, helpful }),
      });
      setFeedback(response.ok ? "saved" : "error");
    } catch {
      setFeedback("error");
    }
  }

  return (
    <footer className="mt-10 border-t border-spark-light-gray pt-5">
      <p className="text-xs text-spark-gray">Last reviewed {reviewedDate(updatedAt)}</p>
      {related.length ? (
        <div className="mt-4">
          <p className="text-[11px] font-bold uppercase tracking-[.08em] text-spark-gray">Related articles</p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {related.map((topic) => (
              <li key={topic.slug}>
                <Link
                  href={`/docs/${topic.slug}`}
                  className="inline-flex rounded-full border border-spark-light-gray px-3 py-1 text-xs font-semibold text-spark-blue-text transition-colors hover:bg-spark-hover"
                >
                  {topic.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="mt-5 flex flex-wrap items-center gap-2 rounded-lg bg-spark-hover px-4 py-3">
        {feedback === "saved" ? (
          <p className="text-sm font-semibold text-spark-green-text">Thanks—your feedback was recorded.</p>
        ) : (
          <>
            <p className="mr-1 text-sm font-semibold text-spark-charcoal">Was this article helpful?</p>
            <button
              type="button"
              disabled={feedback === "sending"}
              onClick={() => sendFeedback(true)}
              className="rounded-md border border-spark-light-gray bg-white px-3 py-1 text-xs font-semibold text-spark-charcoal hover:border-spark-blue disabled:opacity-50"
            >
              Yes
            </button>
            <button
              type="button"
              disabled={feedback === "sending"}
              onClick={() => sendFeedback(false)}
              className="rounded-md border border-spark-light-gray bg-white px-3 py-1 text-xs font-semibold text-spark-charcoal hover:border-spark-blue disabled:opacity-50"
            >
              No
            </button>
            {feedback === "error" ? <span className="text-xs text-spark-red-text">Couldn&apos;t record feedback. Please try again.</span> : null}
          </>
        )}
      </div>
    </footer>
  );
}
